import * as React from 'react'
import Form from 'react-bootstrap/Form'
import useSections from 'api/hooks/use-sections'

type ParentModuleDropdownProps = {
  params: {
    section_id: string,
  },
  setParams: Function,
  hasAllOption?: boolean
}

type Section = {
  id: string,
  name: string,
  unit: string,
  parent_module?: string
}

function ParentModuleDropdown({ params, setParams, hasAllOption = false }: ParentModuleDropdownProps) {
  const { isSuccess: isSectionSuccess, data: sections } = useSections({ options: { refetchOnWindowFocus: false } })

  // Extract unique parent modules from sections
  const parentModules = React.useMemo(() => {
    if (!isSectionSuccess || !sections) return []
    const modules = (sections as Section[])
      .map(s => s.parent_module)
      .filter((pm): pm is string => pm !== undefined && pm !== null && pm.trim() !== '')
    return Array.from(new Set(modules)).sort()
  }, [isSectionSuccess, sections])

  // Determine which parent module is currently selected based on section_id param
  const selectedParentModule = React.useMemo(() => {
    if (!params.section_id || !isSectionSuccess || !sections) return ''

    // Get the first section ID from the param (could be comma-separated)
    const firstSectionId = params.section_id.split(',')[0]
    const section = (sections as Section[]).find(s => s.id === firstSectionId)
    return section?.parent_module || ''
  }, [params.section_id, isSectionSuccess, sections])

  const handleChange = (parentModule: string) => {
    if (!parentModule) {
      // Clear filter
      setParams((prev) => ({
        ...prev,
        section_id: '',
        page: 1,
        analytics: []
      }))
    } else {
      // Get all section IDs that match this parent module
      const sectionIds = (sections as Section[])
        .filter(s => s.parent_module === parentModule)
        .map(s => s.id)
        .join(',')

      setParams((prev) => ({
        ...prev,
        section_id: sectionIds,
        page: 1,
        analytics: []
      }))
    }
  }

  return (
    <>
      {isSectionSuccess && (
        <Form.Group>
          <Form.Select
            as="select"
            value={selectedParentModule}
            className={selectedParentModule ? 'text-primary border-primary' : 'text-info'}
            onChange={(e) => handleChange(e.target.value)}
          >
            {hasAllOption && <option value="">Select Module</option>}
            {parentModules.map((module) => (
              <option key={`parent-module-option-${module}`} value={module}>
                {module}
              </option>
            ))}
          </Form.Select>
        </Form.Group>
      )}
    </>
  )
}

export default ParentModuleDropdown
